<script>
  import { rowClick } from '../lib/row-link.js';
  import Layout from '../components/Layout.svelte';
  import { companyInvoicesApi } from '../stores/api.js';
  import { activeCompanyId, companiesLoading } from '../stores/company.js';
  import { canWriteInCompany } from '../stores/capabilities.js';
  import { push, querystring, link } from 'svelte-spa-router';
  import { get } from 'svelte/store';
  import { t, tImmediate } from '../i18n/index.js';
  import { FilePlus, Globe, FileText, Wallet, MailOpen, CheckCheck, Search, Download, Loader2 } from '../lib/icons/index.js';
  import InvoiceTabs from '../components/InvoiceTabs.svelte';
  import { unreadInbound, refreshUnread } from '../stores/unread.js';
  import {
    LETTA_FILTER_VALUES, LETTA_FILTER_LABELS, seedLettaFilter, isUnread, readStateIds,
    markReadForCompany,
  } from '../lib/read-state.js';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import StatusBadge from '../components/StatusBadge.svelte';
  import EmptyState from '../components/EmptyState.svelte';
  import PageLoader from '../components/PageLoader.svelte';
  import { isFailedInvoice, formatoLabel } from '../lib/labels.js';
  import InfoHint from '../components/InfoHint.svelte';
  import StatusLegend from '../components/StatusLegend.svelte';
  import RecordPaymentDialog from '../components/RecordPaymentDialog.svelte';
  import RecordPaymentsBulkDialog from '../components/RecordPaymentsBulkDialog.svelte';
  import {
    PAGAMENTO_FILTER_VALUES, PAGAMENTO_FILTER_LABELS, seedPagamentoFilter, isPaymentOpen,
    paymentBadge, paymentTotals, openRows,
  } from '../lib/payment-status.js';

  let invoices = [];
  let loading = true;
  let error = null;

  const currentYear = new Date().getFullYear();
  // Offer the current year and the five previous ones.
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  // The Dashboard's "N da controllare" counters link here with
  // ?direction=…&status=failed&year=all&formato=all. Read once, synchronously,
  // before the reactive loaders below fire, so the very first request already
  // carries the filters instead of fetching the whole list and re-fetching a
  // moment later.
  //
  // All four filters are seeded, not just two: this page defaults to the
  // current year and to FPR12, so a link that named only direction+status
  // landed on a table that hid every scarto from last year and every PA
  // invoice — fewer rows than the counter had just promised.
  const initialQuery = new URLSearchParams(get(querystring) || '');
  const initialDirection = ['inbound', 'all'].includes(initialQuery.get('direction'))
    ? initialQuery.get('direction')
    : 'outbound';

  // Pseudo-status: "da controllare" is a bucket of several real statuses
  // (scarto, rifiutata dal destinatario, errore…), so it cannot be sent to the
  // API as ?status= — it is applied to the loaded rows instead.
  const FAILED_FILTER = 'failed';

  /** Empty string = "nessun filtro" for both the year and the formato. */
  const ALL = '';

  /** `?year=all` → no year filter; a listed year → that year; anything else →
   *  the current year, so a hand-edited URL cannot leave the select blank. */
  function seedYear(raw) {
    if (raw === 'all') return ALL;
    const y = parseInt(raw, 10);
    return yearOptions.includes(y) ? y : currentYear;
  }

  /** `?formato=all` → both formati; FPA12 → PA; anything else → FPR12. */
  function seedFormato(raw) {
    if (raw === 'all') return ALL;
    return raw === 'FPA12' ? 'FPA12' : 'FPR12';
  }

  function seedPeriod(raw) {
    if (['quarter', 'month', 'range'].includes(raw)) return raw;
    if (initialQuery.has('from') || initialQuery.has('to')) return 'range';
    return 'year';
  }

  let filterStatus = initialQuery.get('status') === FAILED_FILTER ? FAILED_FILTER : '';
  let filterDirection = initialDirection;
  let filterFormato = initialQuery.has('formato') ? seedFormato(initialQuery.get('formato')) : 'FPR12';
  let filterYear = initialQuery.has('year') ? seedYear(initialQuery.get('year')) : currentYear;
  let filterPeriod = seedPeriod(initialQuery.get('period'));
  let filterQuarter = ['1', '2', '3', '4'].includes(initialQuery.get('quarter')) ? initialQuery.get('quarter') : '1';
  let filterMonth = /^([1-9]|1[0-2])$/.test(initialQuery.get('month') || '') ? initialQuery.get('month') : '1';
  let filterFrom = initialQuery.get('from') || '';
  let filterTo = initialQuery.get('to') || '';
  let filterReceiptFrom = initialQuery.get('ricevutaDal') || '';
  let filterReceiptTo = initialQuery.get('ricevutaAl') || '';
  let filterLateArrivals = initialDirection === 'inbound' && initialQuery.get('arrivateInRitardo') === 'true';
  let filterLatePeriod = ['quarter', 'month'].includes(initialQuery.get('periodoRitardo'))
    ? initialQuery.get('periodoRitardo') : 'quarter';
  $: isLateView = filterDirection === 'inbound' && filterLateArrivals;
  if (!filterYear && ['quarter', 'month'].includes(filterPeriod)) filterPeriod = 'year';

  // «Da saldare», «Scadute», «Saldate»: applicato dal server prima del limite,
  // così con «Tutti gli anni» l'elenco mostra davvero ogni fattura aperta.
  let filterPagamento = seedPagamentoFilter(initialQuery.get('pagamento') || '');

  // «Da leggere»: solo nella scheda Ricevute, applicato dal server prima del limite.
  let filterLetta = seedLettaFilter(initialQuery.get('letta') || '');

  /** Righe chieste in una volta; la riga dei totali dice quando ci si è fermati qui. */
  const LIST_LIMIT = 500;

  /** La fattura di cui è aperto «Registra pagamento», oppure null. */
  let paying = null;
  let bulkOpen = false;
  /** Le fatture aperte spuntate per «Registra pagamenti». */
  let selectedIds = [];
  let downloadingXml = false;
  let xmlExportError = null;
  let xmlExportNotice = '';

  let report = null;
  let reportLoading = false;
  let reportError = null;

  // Exactly the InvoiceStatus enum from src/domain/state-machine.js. The four
  // extra values that used to close this list ('accepted', 'rejected',
  // 'send_failed', 'notification_error') are not states any transition can
  // reach, so picking one always emptied the table.
  const statuses = [
    '', FAILED_FILTER,
    'draft',
    'created', 'xml_generated', 'queued_for_send', 'sent',
    'accepted_by_transport', 'rejected_by_transport', 'sdi_received',
    'delivered', 'notifica_mancata_consegna',
    'scarto', 'decorrenza_termini',
    'esito_cedente_accettato', 'esito_cedente_rifiutato',
    'completed', 'imported', 'error',
    'received', 'received_processed', 'received_rejected',
    'esito_sent', 'esito_scarto',
  ];

  // Everything on this page is scoped to the company chosen in the sidebar
  // switcher. The cross-tenant /api/admin twins used to be called here: they
  // answer 403 to anyone without the global admin role, and show an admin
  // every other company's invoices in the same table.
  $: companyId = $activeCompanyId;

  async function loadInvoices() {
    if (!companyId) return;
    loading = true;
    error = null;
    try {
      const lateView = filterDirection === 'inbound' && filterLateArrivals;
      const period = lateView ? {} : invoicePeriodParams();
      const result = await companyInvoicesApi.list(companyId, {
        status: filterStatus && filterStatus !== FAILED_FILTER ? filterStatus : undefined,
        direction: filterDirection === 'all' ? undefined : filterDirection,
        formatoTrasmissione: filterDirection === 'outbound' && filterFormato ? filterFormato : undefined,
        ...period,
        pagamento: filterPagamento || undefined,
        letta: filterDirection === 'inbound' && filterLetta ? filterLetta : undefined,
        ricevutaDal: filterDirection === 'inbound' ? (filterReceiptFrom || undefined) : undefined,
        ricevutaAl: filterDirection === 'inbound' ? (filterReceiptTo || undefined) : undefined,
        arrivateInRitardo: lateView ? 'true' : undefined,
        periodoRitardo: lateView ? filterLatePeriod : undefined,
        limit: LIST_LIMIT,
      });
      invoices = Array.isArray(result) ? result : [];
      selectedIds = [];
    } catch (err) {
      error = err;
    } finally {
      loading = false;
    }
  }

  function invoicePeriodParams() {
    if (filterPeriod === 'range') {
      return { from: filterFrom || undefined, to: filterTo || undefined };
    }
    if (filterPeriod === 'quarter' && filterYear) {
      const firstMonth = (Number(filterQuarter) - 1) * 3;
      return {
        year: filterYear,
        from: `${filterYear}-${String(firstMonth + 1).padStart(2, '0')}-01`,
        to: new Date(Date.UTC(Number(filterYear), firstMonth + 3, 0)).toISOString().slice(0, 10),
      };
    }
    if (filterPeriod === 'month' && filterYear) {
      const month = Number(filterMonth);
      return {
        year: filterYear,
        from: `${filterYear}-${String(month).padStart(2, '0')}-01`,
        to: new Date(Date.UTC(Number(filterYear), month, 0)).toISOString().slice(0, 10),
      };
    }
    return { year: filterYear || undefined };
  }

  async function loadReport() {
    // The IVA summary is a per-year figure by definition (it feeds the LIPE),
    // so "tutti gli anni" has no report — the endpoint would silently answer
    // for the current year and the numbers would not match the table.
    if (filterDirection !== 'outbound' || filterPeriod === 'range' || !companyId || !filterYear) {
      report = null;
      reportLoading = false;
      return;
    }
    reportLoading = true;
    reportError = null;
    report = null;
    try {
      report = await companyInvoicesApi.quarterlyReport(companyId, {
        year: filterYear,
        formatoTrasmissione: filterFormato || undefined,
      });
    } catch (err) {
      reportError = err;
      report = null;
    } finally {
      reportLoading = false;
    }
  }

  async function reloadAll() {
    await Promise.all([loadInvoices(), loadReport(), refreshUnread(companyId)]);
  }

  // Loads on mount and again whenever the active company changes — a deep link
  // opened in a fresh browser mounts this page before loadCompanies() has
  // resolved, so waiting for the id is the only way the first request carries
  // one.
  $: if (companyId) reloadAll();
  // Still waiting on the company list means still loading — showing "non hai
  // ancora fatture" for that moment would be a lie.
  $: if (!companyId) { invoices = []; report = null; loading = $companiesLoading; }

  // "Da controllare" is applied here rather than by the API, which only knows
  // how to filter one status at a time.
  $: visibleInvoices = filterStatus === FAILED_FILTER
    ? invoices.filter(isFailedInvoice)
    : invoices;

  /**
   * Client-side search over the rows already loaded: invoice number (and SdI
   * identifier), counterpart name and amount. Amounts match on their digits,
   * so "1.280" finds 1.280,00 € and so does "1280".
   */
  let search = '';
  const digits = (value) => String(value ?? '').replace(/[^0-9]/g, '');
  function matchesSearch(inv, query) {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    const haystack = [inv.invoiceNumber, inv.progressivoInvio, inv.identificativoSdi, counterpartyLabel(inv)]
      .filter(Boolean).join(' ').toLowerCase();
    if (haystack.includes(needle)) return true;
    const needleDigits = digits(needle);
    if (!needleDigits || needleDigits.length !== needle.replace(/[\s.,€]/g, '').length) return false;
    const total = computeAmounts(inv).total;
    return total != null && digits(Number(total).toFixed(2)).includes(needleDigits);
  }
  $: shownInvoices = search ? visibleInvoices.filter((inv) => matchesSearch(inv, search)) : visibleInvoices;

  // Summed over the rows shown; partial when the API returned the whole page,
  // however many of them "Da controllare" then keeps.
  $: totals = paymentTotals(shownInvoices, { fetchedCount: invoices.length, limit: LIST_LIMIT });
  $: selectable = shownInvoices;
  $: selectedRows = selectable.filter((inv) => selectedIds.includes(inv.id));
  // One selection, two actions: the payment takes the open rows, the read state the received ones.
  $: payableSelected = openRows(selectedRows);
  $: readSelected = readStateIds(selectedRows);
  $: allSelected = selectable.length > 0 && selectedRows.length === selectable.length;

  function toggleSelected(id) {
    selectedIds = selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id];
  }

  function toggleAll() {
    selectedIds = allSelected ? [] : selectable.map((inv) => inv.id);
  }

  async function downloadSelectedXml() {
    if (!companyId || selectedRows.length === 0 || downloadingXml) return;
    downloadingXml = true;
    xmlExportError = null;
    xmlExportNotice = '';
    try {
      const { blob, filename, headers } = await companyInvoicesApi.downloadSelectedXml(
        companyId, selectedRows.map((invoice) => invoice.id),
      );
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      const excluded = Number(headers.get('x-fatture-escluse') || 0);
      const unreadable = Number(headers.get('x-fatture-non-leggibili') || 0);
      const notices = [];
      if (excluded === 1) notices.push(tImmediate('invoices.xmlExport.excludedOne'));
      else if (excluded > 1) notices.push(tImmediate('invoices.xmlExport.excluded', { n: excluded }));
      if (unreadable === 1) notices.push(tImmediate('invoices.xmlExport.unreadableOne'));
      else if (unreadable > 1) notices.push(tImmediate('invoices.xmlExport.unreadable', { n: unreadable }));
      xmlExportNotice = notices.join(' ');
    } catch (err) {
      xmlExportError = err;
    } finally {
      downloadingXml = false;
    }
  }

  async function onRecorded() {
    paying = null;
    await loadInvoices();
  }

  async function onBulkDone() {
    bulkOpen = false;
    await loadInvoices();
  }

  let marking = false;
  let readStateError = null;

  function clearReadStateError() {
    readStateError = null;
  }
  // Another company's error does not belong on this one's page.
  $: companyId, clearReadStateError();

  /** Segna come lette le ricevute scelte, o tutte; poi ricarica l'elenco e il conteggio. */
  async function markRead(target) {
    const started = { companyId, target };
    marking = true;
    readStateError = null;
    const { error } = await markReadForCompany(started, {
      setReadState: companyInvoicesApi.setReadState,
      reload: (cid) => Promise.all([loadInvoices(), refreshUnread(cid)]),
      currentCompanyId: () => companyId,
    });
    // Shown only under the company it belongs to: see markReadForCompany().
    if (error && companyId === started.companyId) readStateError = error;
    marking = false;
  }

  function setDirection(dir) {
    if (filterDirection === dir) return;
    if (dir !== 'inbound') filterLateArrivals = false;
    filterDirection = dir;
    reloadAll();
  }

  function setFormato(formato) {
    if (filterFormato === formato) return;
    filterFormato = formato;
    reloadAll();
  }

  // bind:value already holds the real value ('' for "tutti gli anni", a number
  // otherwise), so there is nothing left to parse here — parseInt('') used to
  // turn the "all" choice into NaN.
  function setYear() {
    if (!filterYear && ['quarter', 'month'].includes(filterPeriod)) filterPeriod = 'year';
    reloadAll();
  }

  function setPeriod() {
    if (!filterYear && ['quarter', 'month'].includes(filterPeriod)) filterPeriod = 'year';
    Promise.all([loadInvoices(), loadReport()]);
  }

  function goToInvoice(id) { push(`/invoices/${id}`); }

  function statusLabel(status) {
    if (!status) return '-';
    return tImmediate(`invoices.status.${status}`, {}, status);
  }

  function formatDate(d) {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  function formatReceiptDate(d) {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('it-IT', {
      timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', year: 'numeric',
    });
  }

  function formatEuro(n) {
    if (n == null || Number.isNaN(Number(n))) return '-';
    return Number(n).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /**
   * Display the counterparty (cessionario on outbound, cedente on inbound)
   * using the name extracted into `metadata.cessionario.denominazione` /
   * `metadata.cedente.denominazione`. Falls back to Nome + Cognome for
   * persona-fisica payloads, then to the stored tax id for legacy rows
   * imported before the name extractor was added.
   */
  function counterpartyLabel(inv) {
    const outbound = inv?.direction === 'outbound';
    const key = outbound ? 'cessionario' : 'cedente';
    const party = inv?.metadata?.[key];
    if (party) {
      if (party.denominazione) return party.denominazione;
      const full = [party.nome, party.cognome].filter(Boolean).join(' ').trim();
      if (full) return full;
    }
    return (outbound ? inv.receiverTaxId : inv.senderTaxId) || '-';
  }

  function formatPercent(n) {
    if (n == null || Number.isNaN(Number(n))) return '-';
    return `${Number(n).toFixed(2)}%`;
  }

  const CREDIT_NOTE_CODES = new Set(['TD04', 'TD08']);
  const DEBIT_NOTE_CODES = new Set(['TD05', 'TD09']);
  const AUTOFATTURA_CODES = new Set(['TD16', 'TD17', 'TD18', 'TD19', 'TD20', 'TD21']);

  /** FatturaPA TipoDocumento code for an invoice row — column first, metadata
   *  fallback for rows imported before the column migration. */
  function tipoCode(inv) {
    return inv?.tipoDocumento
      || inv?.metadata?.datiGenerali?.tipoDocumento
      || null;
  }

  /** Italian label shown inside the TipoDocumento badge. Falls back to the
   *  raw code (e.g. "TD99") when the translation is missing so rare document
   *  types stay visible rather than rendering as empty. */
  function tipoLabel(code) {
    if (!code) return '-';
    const key = `invoiceCreate.tipiDocumento.${code}`;
    const translated = $t(key);
    return translated === key ? code : translated;
  }

  /** Badge text: "TD01 · Fattura", or the bare code when it has no label. */
  function tipoBadgeText(code) {
    const label = tipoLabel(code);
    return label === code ? code : `${code} · ${label}`;
  }

  function tipoBadgeClass(code) {
    if (!code) return 'badge-gray';
    if (CREDIT_NOTE_CODES.has(code)) return 'badge-magenta';
    if (DEBIT_NOTE_CODES.has(code)) return 'badge-yellow';
    if (AUTOFATTURA_CODES.has(code)) return 'badge-blue';
    return 'badge-cyan';
  }

  function tipoTooltip(code) {
    if (CREDIT_NOTE_CODES.has(code)) return $t('invoices.tipoDocumentoBadge.creditNote');
    if (DEBIT_NOTE_CODES.has(code)) return $t('invoices.tipoDocumentoBadge.debitNote');
    if (AUTOFATTURA_CODES.has(code)) return $t('invoices.tipoDocumentoBadge.autofattura');
    return tipoLabel(code);
  }

  /**
   * Compute per-invoice imponibile / IVA / totale from metadata.lineItems
   * when available. If line items are missing (e.g. inbound invoices stored
   * only as XML), fall back to the stored totalAmount and leave VAT blank.
   */
  function computeAmounts(inv) {
    const items = inv?.metadata?.lineItems;
    if (Array.isArray(items) && items.length) {
      let net = 0;
      let vat = 0;
      const rates = new Set();
      for (const it of items) {
        const qty = Number(it.quantita ?? 1);
        const pu = Number(it.prezzoUnitario ?? 0);
        const lineNet = it.prezzoTotale != null ? Number(it.prezzoTotale) : qty * pu;
        const rate = Number(it.aliquotaIVA ?? it.aliquotaIva ?? 0);
        if (!Number.isNaN(lineNet)) net += lineNet;
        if (!Number.isNaN(rate)) rates.add(rate);
        if (!Number.isNaN(lineNet) && !Number.isNaN(rate)) vat += (lineNet * rate) / 100;
      }
      const vatRate = rates.size === 1 ? [...rates][0] : null;
      const total = inv?.totalAmount != null ? Number(inv.totalAmount) : net + vat;
      return { net, vat, vatRate, total };
    }
    return {
      net: null,
      vat: null,
      vatRate: null,
      total: inv?.totalAmount != null ? Number(inv.totalAmount) : null,
    };
  }

  const quarterMonthsKey = { 1: 'invoices.report.monthsQ1', 2: 'invoices.report.monthsQ2', 3: 'invoices.report.monthsQ3', 4: 'invoices.report.monthsQ4' };
</script>

<Layout>
  <div class="view-head">
    <div class="view-head-copy">
      <h1 class="section-title">{$t('invoices.title')}</h1>
      <p class="view-subtitle">{$t('invoices.subtitle')}</p>
    </div>
    {#if $canWriteInCompany}
      <!-- Create-invoice / create-autofattura actions live next to the data
           they produce. -->
      <div class="view-actions">
        <button type="button" class="btn btn-primary gap-2" title={$t('invoices.createNewTooltip')}
          on:click={() => push('/invoices/new')}>
          <FilePlus size={16} aria-hidden="true" />
          <span>{$t('nav.newInvoice')}</span>
        </button>
        <button type="button" class="btn btn-secondary gap-2" title={$t('invoices.createAutofatturaTooltip')}
          on:click={() => push('/autofatture/new')}>
          <Globe size={16} aria-hidden="true" />
          <span>{$t('nav.newAutofattura')}</span>
        </button>
      </div>
    {/if}
  </div>
  <ErrorBanner error={readStateError} />
  <ErrorBanner error={xmlExportError} />
  {#if xmlExportNotice}
    <div class="alert alert-info mb-4" role="status" aria-live="polite">{xmlExportNotice}</div>
  {/if}

  <!-- Tutte / Inviate / Ricevute is a tab set: the active tab is exposed through
       aria-selected instead of colour alone, and Left/Right (plus Home/End)
       move between the two. Bozze is a separate page, linked beside them. -->
  <InvoiceTabs active={filterDirection} count={shownInvoices.length} onSelect={setDirection} />

  <div id="invoices-panel" role="tabpanel" tabindex="-1"
    aria-labelledby="invoices-tab-{filterDirection}">

  <p id="invoices-search-help" class="sr-only">{$t('invoices.search.help')}</p>
  <div class="toolbar">
    <div class="search-field">
      <Search size={17} class="search-icon" aria-hidden="true" />
      <input type="search" class="input" bind:value={search} aria-label={$t('invoices.search.label')}
        placeholder={$t('invoices.search.placeholder')} aria-describedby="invoices-search-help" />
    </div>
    <div class="toolbar-group">
      {#if filterDirection === 'inbound'}
        <label class="field inline-flex items-center gap-2">
          <input type="checkbox" bind:checked={filterLateArrivals} on:change={loadInvoices} />
          <span>{$t('invoices.receiptDate.lateFilter')}<InfoHint term="fattureArrivateInRitardo" /></span>
        </label>
        {#if filterLateArrivals}
          <label class="field">
            <span>{$t('invoices.receiptDate.period')}</span>
            <select class="input w-auto" bind:value={filterLatePeriod} on:change={loadInvoices}>
              <option value="quarter">{$t('invoices.period.quarter')}</option>
              <option value="month">{$t('invoices.period.month')}</option>
            </select>
          </label>
        {/if}
        <label class="field">
          <span>{$t('invoices.receiptDate.from')}</span>
          <input class="input w-auto" type="date" bind:value={filterReceiptFrom} on:change={loadInvoices} />
        </label>
        <label class="field">
          <span>{$t('invoices.receiptDate.to')}</span>
          <input class="input w-auto" type="date" bind:value={filterReceiptTo} on:change={loadInvoices} />
        </label>
      {/if}
      {#if !isLateView && filterPeriod !== 'range'}
        <label class="field">
          <span>{$t('invoices.year')}</span>
          <select class="input w-auto" bind:value={filterYear} on:change={setYear}>
            <option value={ALL}>{$t('invoices.allYears')}</option>
            {#each yearOptions as y}
              <option value={y}>{y}</option>
            {/each}
          </select>
        </label>
      {/if}
      {#if !isLateView}
      <label class="field">
        <span>{$t('invoices.period.label')}<InfoHint text={$t('invoices.period.help')} /></span>
        <select class="input w-auto" bind:value={filterPeriod} on:change={setPeriod}>
          <option value="year">{$t('invoices.period.year')}</option>
          <option value="quarter" disabled={!filterYear}>{$t('invoices.period.quarter')}</option>
          <option value="month" disabled={!filterYear}>{$t('invoices.period.month')}</option>
          <option value="range">{$t('invoices.period.range')}</option>
        </select>
      </label>
      {/if}
      {#if !isLateView && filterPeriod === 'quarter' && filterYear}
        <label class="field">
          <span>{$t('invoices.period.quarter')}</span>
          <select class="input w-auto" bind:value={filterQuarter} on:change={loadInvoices}>
            <option value="1">{$t('invoices.period.quarters.1')}</option>
            <option value="2">{$t('invoices.period.quarters.2')}</option>
            <option value="3">{$t('invoices.period.quarters.3')}</option>
            <option value="4">{$t('invoices.period.quarters.4')}</option>
          </select>
        </label>
      {:else if !isLateView && filterPeriod === 'month' && filterYear}
        <label class="field">
          <span>{$t('invoices.period.month')}</span>
          <select class="input w-auto" bind:value={filterMonth} on:change={loadInvoices}>
            {#each Array.from({ length: 12 }, (_, i) => String(i + 1)) as month}
              <option value={month}>{$t(`invoices.period.months.${month}`)}</option>
            {/each}
          </select>
        </label>
      {:else if !isLateView && filterPeriod === 'range'}
        <label class="field">
          <span>{$t('invoices.period.from')}</span>
          <input class="input w-auto" type="date" bind:value={filterFrom} on:change={loadInvoices} />
        </label>
        <label class="field">
          <span>{$t('invoices.period.to')}</span>
          <input class="input w-auto" type="date" bind:value={filterTo} on:change={loadInvoices} />
        </label>
      {/if}
      {#if !isLateView && !filterYear && filterPeriod !== 'range'}
        <span class="text-xs text-muted" role="status">{$t('invoices.period.allYearsHint')}</span>
      {/if}
      <label class="field">
        <span>{$t('invoices.col.status')}</span>
        <select class="input w-auto" bind:value={filterStatus} on:change={loadInvoices}>
          {#each statuses as s}
            <option value={s}>
              {#if !s}{$t('invoices.allStatuses')}
              {:else if s === FAILED_FILTER}{$t('invoices.failedFilter')}
              {:else}{statusLabel(s)}{/if}
            </option>
          {/each}
        </select>
      </label>
      <label class="field">
        <span>{$t('invoices.pagamento.filter.label')}</span>
        <select class="input w-auto" bind:value={filterPagamento} on:change={loadInvoices}
          title={$t('invoices.pagamento.filter.help')}>
          {#each PAGAMENTO_FILTER_VALUES as value}
            <option {value}>{$t(PAGAMENTO_FILTER_LABELS[value])}</option>
          {/each}
        </select>
      </label>
      {#if filterDirection === 'inbound'}
        <label class="field">
          <span>{$t('invoices.readState.filter.label')}<InfoHint term="daLeggere" /></span>
          <select class="input w-auto" bind:value={filterLetta} on:change={loadInvoices}
            title={$t('invoices.readState.filter.help')}>
            {#each LETTA_FILTER_VALUES as value}
              <option {value}>{$t(LETTA_FILTER_LABELS[value])}</option>
            {/each}
          </select>
        </label>
      {/if}
    </div>
    <div class="toolbar-group">
      <span class="text-sm text-muted">{$t('invoices.xmlExport.label')}<InfoHint text={$t('invoices.xmlExport.description')} /></span>
      {#if selectedRows.length > 0}
        <button type="button" class="btn btn-primary btn-sm gap-1.5" disabled={downloadingXml}
          on:click={downloadSelectedXml}>
          {#if downloadingXml}
            <Loader2 size={14} class="animate-spin" aria-hidden="true" />
          {:else}
            <Download size={14} aria-hidden="true" />
          {/if}
          <span>{$t('invoices.xmlExport.action', { n: selectedRows.length })}</span>
        </button>
      {/if}
    </div>
    {#if $canWriteInCompany && (payableSelected.length > 0 || readSelected.length > 0 || (filterDirection === 'inbound' && $unreadInbound > 0))}
      <div class="toolbar-group">
        {#if readSelected.length > 0}
          <button type="button" class="btn btn-secondary btn-sm gap-1.5"
            title={$t('invoices.readState.markSelectedTooltip')} disabled={marking}
            on:click={() => markRead({ ids: readSelected })}>
            <MailOpen size={14} aria-hidden="true" />
            <span>{$t('invoices.readState.markSelected', { n: readSelected.length })}</span>
          </button>
        {/if}
        {#if filterDirection === 'inbound' && $unreadInbound > 0}
          <button type="button" class="btn btn-secondary btn-sm gap-1.5"
            title={$t('invoices.readState.markAllTooltip')} disabled={marking}
            on:click={() => markRead({ all: true })}>
            <CheckCheck size={14} aria-hidden="true" />
            <span>{$t('invoices.readState.markAll')}</span>
          </button>
        {/if}
        {#if payableSelected.length > 0}
          <button type="button" class="btn btn-primary btn-sm gap-1.5" on:click={() => (bulkOpen = true)}>
            <Wallet size={14} aria-hidden="true" />
            <span>{$t('invoices.pagamento.bulk.action', { n: payableSelected.length })}</span>
          </button>
        {/if}
      </div>
    {/if}
  </div>

  {#if filterDirection === 'outbound'}
    <div class="flex flex-wrap items-center gap-3 mb-5">
      <span id="formato-label" class="text-sm text-muted">{$t('invoices.subtab.label')}</span>
      <div class="segmented" role="group" aria-labelledby="formato-label">
        <button type="button" aria-pressed={filterFormato === ALL} on:click={() => setFormato(ALL)}
          title={$t('invoices.subtab.allDescription')}>{$t('invoices.subtab.all')}</button>
        <button type="button" aria-pressed={filterFormato === 'FPR12'} on:click={() => setFormato('FPR12')}
          title={$t('invoices.subtab.b2bDescription')}>{$t('invoices.subtab.b2b')}</button>
        <button type="button" aria-pressed={filterFormato === 'FPA12'} on:click={() => setFormato('FPA12')}
          title={$t('invoices.subtab.paDescription')}>{$t('invoices.subtab.pa')}</button>
      </div>
      <details class="help-disclosure w-full">
        <summary>{$t('invoices.formatHelp.summary')}</summary>
        <dl class="help-list">
          <div><dt>{$t('invoices.formatHelp.fpr12Term')}</dt><dd>{$t('invoices.formatHelp.fpr12')}</dd></div>
          <div><dt>{$t('invoices.formatHelp.fpa12Term')}</dt><dd>{$t('invoices.formatHelp.fpa12')}</dd></div>
        </dl>
      </details>
    </div>

    {#if filterPeriod !== 'range'}
    <section class="mb-6" aria-labelledby="iva-report-title">
      <div class="section-heading">
        <h2 id="iva-report-title">
          {filterYear ? $t('invoices.report.title', { year: filterYear }) : $t('invoices.report.titleNoYear')}
        </h2>
        {#if filterYear && !reportError}
          <p class="measure">{$t('invoices.report.scope', { year: filterYear })}</p>
        {/if}
      </div>
      {#if !filterYear}
        <!-- Il riepilogo IVA è per definizione annuale (serve alla LIPE): con
             "tutti gli anni" selezionato non c'è un totale sensato da mostrare,
             e inventarne uno sarebbe peggio che spiegarlo. -->
        <p class="text-sm text-muted">{$t('invoices.report.pickYear')}</p>
      {:else if reportError}
        <ErrorBanner error={reportError} />
      {:else if reportLoading}
        <!-- Nessun totale provvisorio mentre il riepilogo dell'anno si carica. -->
      {:else if report}
        <dl class="summary-strip">
          {#each (report.quarters ?? []) as q}
            <div class="summary-item">
              <dt class="summary-label">
                {$t('invoices.report.quarterShort', { q: q.quarter })} · {$t(quarterMonthsKey[q.quarter])}
                <span class="sr-only">— {$t('invoices.report.totaleImponibile')}</span>
              </dt>
              <dd class="summary-value">{formatEuro(q.totaleImponibile)}</dd>
              <dd class="summary-note">
                {$t('invoices.report.totaleIva')} {formatEuro(q.totaleIva)} ·
                {q.count === 1 ? $t('invoices.report.countOne', { n: q.count }) : $t('invoices.report.count', { n: q.count })}
              </dd>
            </div>
          {/each}
        </dl>
      {/if}
    </section>
    {/if}
  {/if}

  {#if loading}
    <PageLoader />
  {:else if error}
    <ErrorBanner {error} />
  {:else}
    {#if visibleInvoices.length === 0}
      {#if filterStatus || filterPagamento || filterLetta || isLateView || filterReceiptFrom || filterReceiptTo || filterPeriod !== 'year'}
        <div class="p-8 text-center text-muted">{$t('invoices.noMatch')}</div>
      {:else}
        <EmptyState icon={FileText} title={$t('invoices.emptyTitle')} body={$t('invoices.emptyBody')}>
          {#if $canWriteInCompany}
            <button type="button" class="btn btn-primary btn-sm gap-1.5"
              title={$t('invoices.createNewTooltip')}
              on:click={() => push('/invoices/new')}>
              <FilePlus size={14} aria-hidden="true" />
              <span>{$t('invoices.emptyCta')}</span>
            </button>
          {/if}
        </EmptyState>
      {/if}
    {:else if shownInvoices.length === 0}
      <div class="p-8 text-center text-muted" role="status">{$t('invoices.search.noMatch')}</div>
    {:else}
      <StatusLegend invoices={shownInvoices} />
      <!-- On a phone the header row is hidden, so its controls live here instead:
           visible, in the tab order, and only where they make sense. -->
      <div class="cards-toolbar">
        {#if selectable.length > 0}
          <label class="cards-select-all">
            <input type="checkbox" checked={allSelected} on:change={toggleAll}
              title={$t('invoices.selection.selectAll')} />
            <span>{$t('invoices.selection.selectAllShort')}</span>
          </label>
        {/if}
        <span class="cards-help">{$t('invoices.cards.paymentHelp')}<InfoHint term="statoPagamento" /></span>
      </div>
      <div class="overflow-x-auto">
        <table class="table-modern table-stack table-cards cards-invoices" role="table">
          <thead class="table-header" role="rowgroup">
            <tr role="row">
              <th class="table-cell w-8" role="columnheader">
                <input type="checkbox" checked={allSelected} disabled={selectable.length === 0}
                  aria-label={$t('invoices.selection.selectAll')} on:change={toggleAll} />
              </th>
              <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.col.invoiceDate')}</th>
              {#if filterDirection !== 'outbound'}
                <th class="table-cell table-header-cell text-left" role="columnheader">
                  {$t('invoices.receiptDate.label')}<InfoHint term="dataRicezioneSdi" />
                </th>
              {/if}
              <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.col.invoiceNumber')}</th>
              <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.col.tipoDocumento')}</th>
              <th class="table-cell table-header-cell text-left" role="columnheader">{$t(filterDirection === 'outbound' ? 'invoices.col.receiver' : filterDirection === 'inbound' ? 'invoices.col.sender' : 'invoices.col.counterparty')}</th>
              <th class="table-cell table-header-cell text-right" role="columnheader" title={$t('invoices.col.amountsHelp')}>{$t('invoices.col.amounts')}</th>
              <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.col.status')}</th>
              <th class="table-cell table-header-cell text-left" role="columnheader">
                {$t('invoices.pagamento.col')}<InfoHint term="statoPagamento" />
              </th>
            </tr>
          </thead>
          <tbody role="rowgroup">
            {#each shownInvoices as inv (inv.id)}
              {@const amounts = computeAmounts(inv)}
              {@const tipo = tipoCode(inv)}
              {@const isCredit = CREDIT_NOTE_CODES.has(tipo)}
              {@const unread = isUnread(inv)}
              <!-- The whole row opens the invoice for a pointer; the keyboard
                   path is the invoice number, which is a real link. -->
              <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_noninteractive_element_interactions -->
              <tr class="table-row {unread ? 'font-bold' : ''}" role="row" on:click={(event) => rowClick(event, () => goToInvoice(inv.id))}>
                <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_noninteractive_element_interactions -->
                <td class="table-cell cell-select" role="cell" data-label={$t('invoices.selection.select')} on:click|stopPropagation>
                  <input type="checkbox" checked={selectedIds.includes(inv.id)}
                    aria-label={$t('invoices.selection.selectOne', { numero: inv.invoiceNumber || inv.progressivoInvio || '-' })}
                    on:change={() => toggleSelected(inv.id)} />
                </td>
                <td class="table-cell cell-date text-xs mono" role="cell" data-label={$t('invoices.col.invoiceDate')}>{formatDate(inv.invoiceDate || inv.createdAt)}</td>
                {#if filterDirection !== 'outbound'}
                  <td class="table-cell cell-date text-xs mono" role="cell" data-label={$t('invoices.receiptDate.label')}
                    title={inv.direction === 'inbound' && !inv.dataRicezioneSdi && inv.metadata?.imported ? $t('invoices.receiptDate.importedUnavailable') : undefined}>
                    {inv.direction !== 'inbound' ? '-'
                      : inv.dataRicezioneSdi ? formatReceiptDate(inv.dataRicezioneSdi)
                        : inv.metadata?.imported ? $t('invoices.receiptDate.notProvided') : '-'}
                  </td>
                {/if}
                <td class="table-cell cell-number" role="cell" data-label={$t('invoices.col.invoiceNumber')}>
                  {#if unread}
                    <span class="inline-flex items-center gap-1 mr-2 eyebrow eyebrow-sm align-middle"
                      style="color: var(--color-secondary);" title={$t('invoices.readState.newTooltip')}>
                      <span class="inline-block w-2 h-2 rounded-full" style="background-color: var(--color-secondary);" aria-hidden="true"></span>
                      {$t('invoices.readState.new')}
                    </span>
                  {/if}
                  <a href="/invoices/{inv.id}" use:link class="invoice-link"
                    aria-label={$t('invoices.openInvoice', { numero: inv.invoiceNumber || inv.progressivoInvio || '-' }) + (unread ? ' · ' + $t('invoices.readState.newLabel') : '')}>
                    {inv.invoiceNumber || inv.progressivoInvio || '-'}
                  </a>
                  {#if inv.identificativoSdi}
                    <span class="cell-secondary" title={$t('invoices.col.sdiId') + ': ' + inv.identificativoSdi}>
                      {$t('invoices.col.sdiShort', { id: inv.identificativoSdi })}
                    </span>
                  {/if}
                </td>
                <!-- The long document-type labels wrap inside the badge and the cell is capped, so
                     one TD17 does not widen the whole column. The format sits underneath. -->
                <td class="table-cell cell-fiscal" role="cell" data-label={$t('invoices.col.tipoDocumento')}>
                  <div class="max-w-[10rem]">
                    {#if tipo}
                      <span class="badge whitespace-normal {tipoBadgeClass(tipo)}" title={tipoTooltip(tipo)}>{tipoBadgeText(tipo)}</span>
                    {:else}
                      <span class="text-xs text-muted">-</span>
                    {/if}
                    {#if inv.formatoTrasmissione}
                      <span class="cell-secondary mt-1" title={formatoLabel(inv.formatoTrasmissione)}>{inv.formatoTrasmissione}</span>
                    {/if}
                  </div>
                </td>
                <!-- A block with a max width, not a bare inline span (which ignores it): a long
                     ragione sociale would otherwise push the payment action off screen. -->
                <td class="table-cell cell-party" role="cell" data-label={$t(filterDirection === 'outbound' ? 'invoices.col.receiver' : filterDirection === 'inbound' ? 'invoices.col.sender' : 'invoices.col.counterparty')}>
                  <span class="block line-clamp-2 max-w-[11rem] whitespace-normal break-words cell-strong" title={counterpartyLabel(inv)}>{counterpartyLabel(inv)}</span>
                </td>
                <!-- Total on top, imponibile and IVA on their own lines below: every value is still
                     there, in one cell. Each line is a block and each value a nowrap span; the lines
                     break between values, never inside one. -->
                <td class="table-cell cell-amount text-right tabular" role="cell" data-label={$t('invoices.col.amounts')}>
                  <span class="block whitespace-nowrap cell-strong" class:text-accent={isCredit}>{formatEuro(amounts.total)}</span>
                  <span class="cell-secondary">{$t('invoices.col.netAmount')} {formatEuro(amounts.net)}</span>
                  <span class="cell-secondary">{$t('invoices.col.vatAmount')} {formatPercent(amounts.vatRate)} {formatEuro(amounts.vat)}</span>
                </td>
                <!-- Capped like the Tipo cell: the longest status labels wrap. -->
                <td class="table-cell cell-status" role="cell" data-label={$t('invoices.col.status')}>
                  <div class="max-w-[10rem]">
                    <StatusBadge status={inv.status} invoice={inv} />
                  </div>
                </td>
                <td class="table-cell cell-payment" role="cell" data-label={$t('invoices.pagamento.col')}>
                  {#if paymentBadge(inv.pagamento, inv.direction)}
                    {@const badge = paymentBadge(inv.pagamento, inv.direction)}
                    <div class="flex flex-col items-start gap-1">
                      <span class="badge {badge.cls}" title={$t(`invoices.pagamento.statoHelp.${inv.pagamento.stato}`)}>{$t(badge.key)}</span>
                      {#if inv.pagamento.residuo > 0}
                        <span class="cell-secondary">{$t('invoices.pagamento.residuo', { importo: formatEuro(inv.pagamento.residuo) })}</span>
                      {/if}
                      {#if $canWriteInCompany && isPaymentOpen(inv.pagamento)}
                        <!-- Icon only, to keep the Pagamento column narrow; the name is in aria-label. -->
                        <button type="button" class="btn btn-secondary btn-sm w-9 !p-0"
                          title={$t('invoices.pagamento.recordTooltip')}
                          aria-label={$t('invoices.pagamento.recordFor', { numero: inv.invoiceNumber || inv.progressivoInvio || '-' })}
                          on:click|stopPropagation={() => (paying = inv)}
                          on:keydown|stopPropagation>
                          <Wallet size={14} aria-hidden="true" />
                        </button>
                      {/if}
                    </div>
                  {:else}
                    <span class="text-xs text-muted" title={$t('invoices.pagamento.statoHelp.nonApplicabile')}>-</span>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      <div class="table-footnote" role="status">
        {#if filterDirection !== 'all'}
          <span>
            {totals.count === 1
              ? $t('invoices.pagamento.totals.lineOne', { importo: formatEuro(totals.residuo) })
              : $t('invoices.pagamento.totals.line', { n: totals.count, importo: formatEuro(totals.residuo) })}
          </span>
        {/if}
        <span>{$t('invoices.shownOf', { shown: shownInvoices.length, total: visibleInvoices.length })}</span>
      </div>
    {/if}
    {#if totals.partial}
      <p class="mt-2 text-xs text-warning" role="status">{$t('invoices.pagamento.totals.partial', { limit: LIST_LIMIT })}</p>
    {/if}
  {/if}
  </div>
  {#if paying}
    <RecordPaymentDialog {companyId} invoice={paying}
      on:recorded={onRecorded} on:refresh={onRecorded} on:cancel={() => (paying = null)} />
  {/if}
  {#if bulkOpen}
    <RecordPaymentsBulkDialog {companyId} invoices={payableSelected}
      on:done={onBulkDone} on:cancel={() => (bulkOpen = false)} />
  {/if}
</Layout>
